import { useState, useEffect, useCallback, useMemo, useRef, Fragment } from 'react';
import { useOutletContext } from 'react-router-dom';
import { sb } from '../lib/supabase';
import { useSupabaseAuth, useAccess } from '../context/AuthContext';
import useWorkspaceAccess from '../hooks/useWorkspaceAccess';
import Loader from '../components/Loader';
import Modal from '../components/Modal';

const SUBTABS = [
  { id: 'grid', label: 'Allocation grid' },
  { id: 'demand', label: 'Demand overview' },
];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS_PER = { month: 21, week: 5 };

const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
function monthPeriods(anchor, n) {
  const out = [];
  for (let i = 0; i < n; i++) {
    const d = new Date(anchor.getFullYear(), anchor.getMonth() + i, 1);
    out.push({ start: ymd(d), label: `${MONTHS[d.getMonth()]} '${pad(d.getFullYear() % 100)}` });
  }
  return out;
}
function weekPeriods(anchor, n) {
  const m = new Date(anchor); const off = (m.getDay() + 6) % 7; m.setDate(m.getDate() - off);
  const out = [];
  for (let i = 0; i < n; i++) { const d = new Date(m); d.setDate(d.getDate() + i * 7); out.push({ start: ymd(d), label: `${pad(d.getDate())} ${MONTHS[d.getMonth()]}` }); }
  return out;
}

export default function Resource() {
  const { programme, projects } = useOutletContext();
  const { profile } = useSupabaseAuth();
  const { isAdmin } = useAccess();
  const { canWrite } = useWorkspaceAccess();
  const orgId = programme?.organisation_id;

  const [tab, setTab] = useState('grid');
  const [people, setPeople] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [lookups, setLookups] = useState({ teams: [], subTeams: [], countries: [] });
  const [projMap, setProjMap] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!orgId) { setLoading(false); return; }
    setLoading(true);
    try {
      const [rRes, aRes, teams, subTeams, countries] = await Promise.all([
        sb.from('profiles_directory').select('*').eq('organisation_id', orgId).order('full_name'),
        sb.from('resource_allocations').select('*'),
        sb.from('resource_teams').select('*').eq('organisation_id', orgId).order('name'),
        sb.from('resource_sub_teams').select('*').eq('organisation_id', orgId).order('name'),
        sb.from('resource_countries').select('*').eq('organisation_id', orgId).order('name'),
      ]);
      setPeople(rRes.data || []);
      setAllocations(aRes.data || []);
      setLookups({
        teams: teams.data || [],
        subTeams: subTeams.data || [],
        countries: countries.data || [],
      });

      // project names for any allocation project ids not in this programme
      const ctxMap = {}; (projects || []).forEach((p) => { ctxMap[p.id] = p.name; });
      const missing = [...new Set((aRes.data || []).map((a) => a.project_id))].filter((id) => !ctxMap[id]);
      if (missing.length) {
        const { data } = await sb.from('projects').select('id, name').in('id', missing);
        (data || []).forEach((p) => { ctxMap[p.id] = p.name; });
      }
      setProjMap(ctxMap);
    } catch (e) {
      console.error('[Resource] load failed', e);
    } finally {
      setLoading(false);
    }
  }, [orgId, projects]);

  useEffect(() => { load(); }, [load]);

  const setAllocation = useCallback(async (profileId, projectId, periodType, periodStart, pct) => {
    const val = Math.max(0, Number(pct) || 0);
    setAllocations((prev) => {
      const i = prev.findIndex((a) => a.profile_id === profileId && a.project_id === projectId && a.period_type === periodType && a.period_start === periodStart);
      if (i >= 0) { const next = [...prev]; next[i] = { ...next[i], allocation_pct: val }; return next; }
      return [...prev, { profile_id: profileId, project_id: projectId, period_type: periodType, period_start: periodStart, allocation_pct: val }];
    });
    await sb.from('resource_allocations').upsert(
      { profile_id: profileId, project_id: projectId, period_type: periodType, period_start: periodStart, allocation_pct: val, created_by: profile?.id || null },
      { onConflict: 'profile_id,project_id,period_type,period_start' }
    );
    if (programme?.id && profileId) {
      await sb.from('programme_members').upsert(
        { programme_id: programme.id, profile_id: profileId, role: 'consultant' },
        { onConflict: 'programme_id,profile_id', ignoreDuplicates: true }
      );
    }
  }, [profile?.id, programme?.id]);

  if (loading) return <div className="content"><Loader text="Loading resources…" /></div>;

  return (
    <div className="content">
      <div className="res-subtabs">
        {SUBTABS.map((t) => <button key={t.id} className={`res-subtab${tab === t.id ? ' active' : ''}`} onClick={() => setTab(t.id)}>{t.label}</button>)}
      </div>

      {tab === 'grid' && (
        <AllocationGrid people={people} projects={projects || []} allocations={allocations} lookups={lookups} setAllocation={setAllocation} reload={load} profile={profile} isAdmin={isAdmin} canWrite={canWrite} />
      )}
      {tab === 'demand' && (
        <DemandOverview people={people} allocations={allocations} projMap={projMap} />
      )}
    </div>
  );
}

// ── Screen 1: Allocation grid ───────────────────────────────────────────────────
const OPTIONAL_COLS = [
  { key: 'role', label: 'Role' }, { key: 'team', label: 'Team' }, { key: 'sub_team', label: 'Sub-team' },
  { key: 'country', label: 'Country' }, { key: 'location', label: 'Location' }, { key: 'blended_rate', label: 'Blended rate' },
];

function AllocationGrid({ people, projects, allocations, lookups, setAllocation, reload, profile, isAdmin, canWrite }) {
  const [periodType, setPeriodType] = useState('month');
  const [unit, setUnit] = useState('pct');
  const [anchor, setAnchor] = useState(() => new Date());
  const [count] = useState(12);
  const [activeProjects, setActiveProjects] = useState(null); // null = all
  const optionalCols = useMemo(
    () => OPTIONAL_COLS.filter((c) => isAdmin || c.key !== 'blended_rate'),
    [isAdmin],
  );
  const [shownCols, setShownCols] = useState([]);
  const [colPickOpen, setColPickOpen] = useState(false);
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });
  const [addOpen, setAddOpen] = useState(false);
  const [sel, setSel] = useState(null); // {key, col}
  const fillRef = useRef(null);
  const [fillHi, setFillHi] = useState(null); // {key, from, to}

  const periods = useMemo(() => (periodType === 'month' ? monthPeriods(anchor, count) : weekPeriods(anchor, count)), [periodType, anchor, count]);
  const nameOf = useCallback((map, id) => lookups[map].find((x) => x.id === id)?.name || '', [lookups]);
  const resById = useMemo(() => { const m = {}; people.forEach((r) => { m[r.id] = r; }); return m; }, [people]);
  const projById = useMemo(() => { const m = {}; projects.forEach((p) => { m[p.id] = p; }); return m; }, [projects]);
  const projectIds = useMemo(() => projects.map((p) => p.id), [projects]);

  // alloc lookup map
  const allocMap = useMemo(() => {
    const m = {};
    allocations.forEach((a) => { m[`${a.profile_id}|${a.project_id}|${a.period_type}|${a.period_start}`] = a.allocation_pct; });
    return m;
  }, [allocations]);

  // resource+period totals across all projects (for over-allocation)
  const resTotals = useMemo(() => {
    const m = {};
    allocations.filter((a) => a.period_type === periodType).forEach((a) => {
      m[`${a.profile_id}|${a.period_start}`] = (m[`${a.profile_id}|${a.period_start}`] || 0) + Number(a.allocation_pct || 0);
    });
    return m;
  }, [allocations, periodType]);

  // rows = distinct (resource, project) from allocations within programme projects
  const rows = useMemo(() => {
    const seen = new Set();
    const out = [];
    allocations.forEach((a) => {
      if (!projectIds.includes(a.project_id)) return;
      const key = `${a.profile_id}|${a.project_id}`;
      if (seen.has(key)) return;
      seen.add(key);
      const r = resById[a.profile_id];
      if (!r) return;
      out.push({ key, resource: r, projectId: a.project_id });
    });
    return out;
  }, [allocations, projectIds, resById]);

  const decorated = useMemo(() => rows.map((row) => ({
    ...row,
    name: row.resource.display_label || row.resource.full_name || '—',
    project: projById[row.projectId]?.name || '',
    role: row.resource.job_title || '',
    team: nameOf('teams', row.resource.team_id),
    sub_team: nameOf('subTeams', row.resource.sub_team_id),
    country: nameOf('countries', row.resource.country_id),
    location: row.resource.location || '',
    blended_rate: row.resource.blended_rate != null ? `${row.resource.currency_code || ''} ${row.resource.blended_rate}` : '',
  })), [rows, projById, nameOf]);

  const visible = useMemo(() => {
    let r = decorated;
    if (activeProjects) r = r.filter((x) => activeProjects.has(x.projectId));
    Object.entries(filters).forEach(([k, v]) => { if (v?.trim()) r = r.filter((x) => `${x[k] || ''}`.toLowerCase().includes(v.toLowerCase())); });
    const dir = sort.dir === 'asc' ? 1 : -1;
    r = [...r].sort((a, b) => (`${a[sort.key] || ''}`.localeCompare(`${b[sort.key] || ''}`)) * dir);
    return r;
  }, [decorated, activeProjects, filters, sort]);

  function toggleProject(id) {
    setActiveProjects((prev) => {
      const base = prev ? new Set(prev) : new Set(projectIds);
      base.has(id) ? base.delete(id) : base.add(id);
      return base.size === projectIds.length ? null : base;
    });
  }
  function toggleSort(key) { setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' })); }
  function disp(pct) {
    if (pct == null) return '';
    if (unit === 'days') return +(pct / 100 * DAYS_PER[periodType]).toFixed(2);
    return pct;
  }
  function toPct(v) { return unit === 'days' ? (Number(v) || 0) / DAYS_PER[periodType] * 100 : (Number(v) || 0); }

  // drag fill
  function startFill(e, row, colIdx) {
    if (!canWrite) return;
    e.preventDefault(); e.stopPropagation();
    const value = allocMap[`${row.resource.id}|${row.projectId}|${periodType}|${periods[colIdx].start}`] ?? 0;
    fillRef.current = { row, fromCol: colIdx, value };
    setFillHi({ key: row.key, from: colIdx, to: colIdx });
    const onUp = () => {
      window.removeEventListener('mouseup', onUp);
      const f = fillRef.current; fillRef.current = null;
      setFillHi((hi) => {
        if (f && hi) { const lo = Math.min(hi.from, hi.to), hg = Math.max(hi.from, hi.to); for (let i = lo; i <= hg; i++) setAllocation(f.row.resource.id, f.row.projectId, periodType, periods[i].start, f.value); }
        return null;
      });
    };
    window.addEventListener('mouseup', onUp);
  }
  function fillEnter(colIdx) { if (fillRef.current) setFillHi((hi) => (hi ? { ...hi, to: colIdx } : hi)); }

  const cols = ['name', 'project', ...shownCols];
  const colLabel = { name: 'Name', project: 'Project', ...Object.fromEntries(optionalCols.map((c) => [c.key, c.label])) };

  return (
    <div>
      <div className="plan-toolbar">
        <div className="plan-seg">
          <button className={periodType === 'month' ? 'on' : ''} onClick={() => setPeriodType('month')}>Monthly</button>
          <button className={periodType === 'week' ? 'on' : ''} onClick={() => setPeriodType('week')}>Weekly</button>
        </div>
        <div className="plan-seg">
          <button className={unit === 'pct' ? 'on' : ''} onClick={() => setUnit('pct')}>%</button>
          <button className={unit === 'days' ? 'on' : ''} onClick={() => setUnit('days')}>Days</button>
        </div>
        <div className="plan-seg">
          <button onClick={() => setAnchor((a) => new Date(a.getFullYear(), a.getMonth() - (periodType === 'month' ? 3 : 0), a.getDate() - (periodType === 'week' ? 21 : 0)))}>‹</button>
          <button onClick={() => setAnchor(new Date())}>Now</button>
          <button onClick={() => setAnchor((a) => new Date(a.getFullYear(), a.getMonth() + (periodType === 'month' ? 3 : 0), a.getDate() + (periodType === 'week' ? 21 : 0)))}>›</button>
        </div>
        <div style={{ position: 'relative' }}>
          <button className="btn-ghost" onClick={() => setColPickOpen((v) => !v)}>Columns ▾</button>
          {colPickOpen && (
            <div className="col-pick">
              {optionalCols.map((c) => (
                <label key={c.key}>
                  <input type="checkbox" checked={shownCols.includes(c.key)} onChange={(e) => setShownCols((prev) => e.target.checked ? [...prev, c.key] : prev.filter((x) => x !== c.key))} />
                  {c.label}
                </label>
              ))}
            </div>
          )}
        </div>
        {canWrite && <button className="btn-primary" style={{ marginLeft: 'auto' }} onClick={() => setAddOpen(true)}>+ Add resource to project</button>}
      </div>

      {projects.length > 0 && (
        <div className="filter-tabs" style={{ marginBottom: 10 }}>
          {projects.map((p) => {
            const on = !activeProjects || activeProjects.has(p.id);
            return <button key={p.id} className={`ftab${on ? ' active' : ''}`} onClick={() => toggleProject(p.id)}>{p.name}</button>;
          })}
        </div>
      )}

      {visible.length === 0 ? (
        <div className="empty">No allocations yet. Use “Add resource to project” to start. (Add people under Admin → Users &amp; roles first.)</div>
      ) : (
        <div className="alloc-scroll">
          <table className="alloc-tbl">
            <thead>
              <tr>
                {cols.map((c, i) => (
                  <th key={c} className={i < 2 ? 'alloc-fixed' : ''} style={i === 0 ? { left: 0, minWidth: 150 } : i === 1 ? { left: 150, minWidth: 120 } : { minWidth: 90 }} onClick={() => toggleSort(c)}>
                    {colLabel[c]}{sort.key === c ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
                  </th>
                ))}
                {periods.map((p) => <th key={p.start} className="alloc-cell">{p.label}</th>)}
              </tr>
              <tr className="alloc-filter-row">
                {cols.map((c, i) => (
                  <th key={c} className={i < 2 ? 'alloc-fixed' : ''} style={i === 0 ? { left: 0 } : i === 1 ? { left: 150 } : undefined}>
                    <input placeholder="filter" value={filters[c] || ''} onChange={(e) => setFilters((f) => ({ ...f, [c]: e.target.value }))} />
                  </th>
                ))}
                {periods.map((p) => <th key={p.start} className="alloc-cell" />)}
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => (
                <tr key={row.key}>
                  {cols.map((c, i) => (
                    <td key={c} className={`${i < 2 ? 'alloc-fixed' : ''} ${c === 'name' ? 'alloc-name' : 'alloc-meta'}`} style={i === 0 ? { left: 0 } : i === 1 ? { left: 150 } : undefined}>{row[c]}</td>
                  ))}
                  {periods.map((p, ci) => {
                    const k = `${row.resource.id}|${row.projectId}|${periodType}|${p.start}`;
                    const pct = allocMap[k];
                    const over = (resTotals[`${row.resource.id}|${p.start}`] || 0) > 100;
                    const hi = fillHi && fillHi.key === row.key && ci >= Math.min(fillHi.from, fillHi.to) && ci <= Math.max(fillHi.from, fillHi.to);
                    const selected = sel && sel.key === row.key && sel.col === ci;
                    return (
                      <td key={p.start} className={`alloc-cell${over ? ' over' : ''}${hi ? ' fillhi' : ''}${selected ? ' sel' : ''}`} onClick={() => setSel({ key: row.key, col: ci })}>
                        <input
                          readOnly={!canWrite}
                          value={pct == null ? '' : (unit === 'pct' ? `${disp(pct)}` : `${disp(pct)}`)}
                          onChange={(e) => setAllocation(row.resource.id, row.projectId, periodType, p.start, toPct(e.target.value.replace('%', '')))}
                          inputMode="decimal"
                        />
                        {canWrite && selected && <div className="fill-handle" onMouseDown={(e) => startFill(e, row, ci)} />}
                        <span onMouseEnter={() => fillEnter(ci)} style={{ position: 'absolute', inset: 0, pointerEvents: fillRef.current ? 'auto' : 'none' }} />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {canWrite && addOpen && (
        <AddAllocationModal people={people} projects={projects} firstPeriod={periods[0]?.start} periodType={periodType} setAllocation={setAllocation} onClose={() => setAddOpen(false)} onSaved={() => { setAddOpen(false); reload(); }} />
      )}
    </div>
  );
}

function AddAllocationModal({ people, projects, firstPeriod, periodType, setAllocation, onClose, onSaved }) {
  const [profileId, setProfileId] = useState(people[0]?.id || '');
  const [projectId, setProjectId] = useState(projects[0]?.id || '');
  const [busy, setBusy] = useState(false);
  async function save() {
    setBusy(true);
    await setAllocation(profileId, projectId, periodType, firstPeriod, 0);
    setBusy(false); onSaved();
  }
  return (
    <Modal title="Add person to project" onClose={onClose} busy={busy}
      footer={<><button className="btn-ghost" onClick={onClose}>Cancel</button><button className="btn-primary" onClick={save} disabled={busy || !profileId || !projectId}>Add row</button></>}>
      {people.length === 0 ? <div className="empty">No people yet. Add them under Admin → Users &amp; roles (+ Add person).</div> : (
        <>
          <div className="form-row"><label className="form-label">Person</label>
            <select className="form-select" value={profileId} onChange={(e) => setProfileId(e.target.value)}>
              {people.map((r) => <option key={r.id} value={r.id}>{r.display_label || r.full_name || '—'}</option>)}
            </select>
          </div>
          <div className="form-row"><label className="form-label">Project</label>
            <select className="form-select" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        </>
      )}
    </Modal>
  );
}

// ── Screen 2: Demand overview ───────────────────────────────────────────────────
function DemandOverview({ people, allocations, projMap }) {
  const [periodType, setPeriodType] = useState('month');
  const [unit, setUnit] = useState('pct');
  const [anchor, setAnchor] = useState(() => new Date());
  const [expanded, setExpanded] = useState(new Set());
  const periods = useMemo(() => (periodType === 'month' ? monthPeriods(anchor, 12) : weekPeriods(anchor, 12)), [periodType, anchor]);
  const resById = useMemo(() => { const m = {}; people.forEach((r) => { m[r.id] = r; }); return m; }, [people]);

  const byRes = useMemo(() => {
    const m = {};
    allocations.filter((a) => a.period_type === periodType).forEach((a) => {
      (m[a.profile_id] ||= { total: {}, projects: {} });
      m[a.profile_id].total[a.period_start] = (m[a.profile_id].total[a.period_start] || 0) + Number(a.allocation_pct || 0);
      (m[a.profile_id].projects[a.project_id] ||= {});
      m[a.profile_id].projects[a.project_id][a.period_start] = (m[a.profile_id].projects[a.project_id][a.period_start] || 0) + Number(a.allocation_pct || 0);
    });
    return m;
  }, [allocations, periodType]);

  const resIds = useMemo(() => Object.keys(byRes).filter((id) => resById[id]), [byRes, resById]);
  function cell(pct) {
    if (pct == null || pct === 0) return { cls: 'zero', txt: '–' };
    const v = unit === 'days' ? +(pct / 100 * DAYS_PER[periodType]).toFixed(1) : Math.round(pct);
    const txt = unit === 'days' ? `${v}` : `${v}%`;
    if (pct > 100) return { cls: 'red', txt };
    if (pct >= 80) return { cls: 'amber', txt };
    if (pct < 50) return { cls: 'low', txt };
    return { cls: '', txt };
  }

  return (
    <div>
      <div className="plan-toolbar">
        <div className="plan-seg">
          <button className={periodType === 'month' ? 'on' : ''} onClick={() => setPeriodType('month')}>Monthly</button>
          <button className={periodType === 'week' ? 'on' : ''} onClick={() => setPeriodType('week')}>Weekly</button>
        </div>
        <div className="plan-seg">
          <button className={unit === 'pct' ? 'on' : ''} onClick={() => setUnit('pct')}>%</button>
          <button className={unit === 'days' ? 'on' : ''} onClick={() => setUnit('days')}>Days</button>
        </div>
        <div className="plan-seg">
          <button onClick={() => setAnchor((a) => new Date(a.getFullYear(), a.getMonth() - (periodType === 'month' ? 3 : 0), a.getDate() - (periodType === 'week' ? 21 : 0)))}>‹</button>
          <button onClick={() => setAnchor(new Date())}>Now</button>
          <button onClick={() => setAnchor((a) => new Date(a.getFullYear(), a.getMonth() + (periodType === 'month' ? 3 : 0), a.getDate() + (periodType === 'week' ? 21 : 0)))}>›</button>
        </div>
        <button className="btn-ghost" onClick={() => setExpanded(new Set(resIds))}>Expand all</button>
        <button className="btn-ghost" onClick={() => setExpanded(new Set())}>Collapse all</button>
      </div>
      {resIds.length === 0 ? <div className="empty">No allocations recorded yet.</div> : (
        <div className="alloc-scroll">
          <table className="alloc-tbl">
            <thead>
              <tr>
                <th className="alloc-fixed" style={{ left: 0, minWidth: 200 }}>Resource</th>
                {periods.map((p) => <th key={p.start} className="demand-cell">{p.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {resIds.map((rid) => {
                const r = resById[rid];
                const data = byRes[rid];
                const open = expanded.has(rid);
                return (
                  <Fragment key={rid}>
                    <tr>
                      <td className="alloc-fixed alloc-name" style={{ left: 0, cursor: 'pointer' }} onClick={() => setExpanded((s) => { const n = new Set(s); n.has(rid) ? n.delete(rid) : n.add(rid); return n; })}>
                        {open ? '▾' : '▶'} {r.display_label || r.full_name || '—'}
                      </td>
                      {periods.map((p) => { const c = cell(data.total[p.start]); return <td key={p.start} className={`demand-cell ${c.cls}`}>{c.txt}</td>; })}
                    </tr>
                    {open && Object.keys(data.projects).map((pid) => (
                      <tr key={`${rid}-${pid}`}>
                        <td className="alloc-fixed alloc-meta" style={{ left: 0, paddingLeft: 26 }}>{projMap[pid] || 'Project'}</td>
                        {periods.map((p) => { const c = cell(data.projects[pid][p.start]); return <td key={p.start} className={`demand-cell ${c.cls}`}>{c.txt}</td>; })}
                      </tr>
                    ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
